import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { FormGroupType } from '../../../../../../../../core/models/form-group-type';
import { ApplicationStateService } from '../../../../../../shared/services/application-state/application-state.service';
import { ScheduleDFormModel } from '../../../../models/schedule-d-form-model';
import { ScheduleDFormService } from '../../../../services/schedule-d-form/schedule-d-form.service';
import { ScheduleDLocationType } from '../../../../types/schedule-d-location-type';
import { ScheduleDStepControlsMap } from '../../../../types/schedule-d-step-controls-map';
import { ScheduleDStepBaseService } from '../../../shared/services/schedule-d-step-base.service';

@Injectable({
    providedIn: 'root',
})
export class ScheduleDStepTwoService extends ScheduleDStepBaseService<ScheduleDLocationType> {
    override stepControlMap: ScheduleDStepControlsMap<ScheduleDLocationType> = {
        fixed: [
            'latitude',
            'longitude',
            'streetAddress',
            'city',
            'state',
            'county',
            'groundElevation',
            'heightOfSupportStructure',
            'overallHeightOfStructure',
            'structureTypeCode',
        ],
        A: ['correspondingFixedLocation', 'radius'],
        C: ['state', 'county'],
        N: [],
        O: ['areaOfOperationText'],
        P: ['latitude', 'longitude', 'radius', 'city', 'state', 'county'],
        S: ['state'],
        T: ['city', 'state'],
        U: [],
        X: ['state', 'county'],
    };

    constructor() {
        super(inject(ScheduleDFormService), inject(ApplicationStateService));
    }

    override getLocationType(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): ScheduleDLocationType | null {
        return this.formService.getLocationType(form);
    }

    /**
     * For 'inside' location steps, we want the user to freely navigate between steps, so
     * always return true here.
     * @param _ The form group (unused in this implementation)
     * @returns Always returns true
     */
    override isStepValid(
        _: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): Observable<boolean> {
        return of(true);
    }

    /**
     * Returns whether the form should show a field based on the type of schedule d.
     * @param field the field to check.
     * @returns true if the form should show the field, otherwise false.
     */
    canHaveField(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
        field: keyof ScheduleDFormModel,
    ): boolean {
        if (this.formService.isFixed(form)) {
            return this.stepControlMap.fixed.includes(field);
        }

        return this.stepControlMap[
            form.controls.areaOfOperationCode.value as ScheduleDLocationType
        ]?.includes(field);
    }
}
